// composables/useAgences.ts
import { createAgenceValidator, patchAgenceValidator, updateAgenceValidator } from '~/validators/agence'
import { Agence } from '~/models/Agence'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import type { OrganizationKey } from '~~/errors/ConcurrentChangeError'
import type { MergeRecord } from '~~/utils/organization/concurrentPatch'

type RawAgence = {
    dirLib: string
    domLib: string
    ageLib: string
    ageLibLong?: string
    ageDesc?: string
    equipesId?: string[]
}

function mapToAgence(raw: RawAgence): Agence {
    return new Agence({
        dirLib: raw.dirLib,
        domLib: raw.domLib,
        ageLib: raw.ageLib,
        ageLibLong: raw.ageLibLong ?? '',
        ageDesc: raw.ageDesc ?? '',
    })
}

export const useAgences = () => {
    async function storeAgence(agenceData: Agence): Promise<Agence | null> {
        const payload = await createAgenceValidator.validate(agenceData)
        const data = await useApiMonOrga<RawAgence>('POST', 'agences', undefined, payload)
        return data ? mapToAgence(data) : null
    }

    async function fetchAgenceById(
        id: string,
    ): Promise<null | { agence: Agence; equipesId: string[] }> {
        const encodedId = encodeURIComponent(id)
        const data = await useApiMonOrga<RawAgence>('GET', `agences/${encodedId}`)

        if (!data) return null

        const agence = mapToAgence(data)

        return {
            agence,
            equipesId: data.equipesId ?? [],
        }
    }

    async function updateAgence(id: string, agenceData: Agence): Promise<Agence | null> {
        const payload = await updateAgenceValidator.validate(agenceData)
        const encodedId = encodeURIComponent(id)
        const data = await useApiMonOrga<RawAgence>('PUT', `agences/${encodedId}`, undefined, payload)
        return data ? mapToAgence(data) : null
    }

    async function patchAgence(
        id: string,
        agenceData: Partial<Agence>,
    ): Promise<Agence | null> {
        const payload = await patchAgenceValidator.validate(agenceData)
        const encodedId = encodeURIComponent(id)
        const data = await useApiMonOrga<RawAgence>('PATCH', `agences/${encodedId}`, undefined, payload)
        return data ? mapToAgence(data) : null
    }

    async function mergeAgence(
        id: string,
        changes: MergeRecord,
        base: MergeRecord,
    ): Promise<{ agence: Agence; currentKey: OrganizationKey | null } | null> {
        const validated = await patchAgenceValidator.validate(changes)
        const encodedId = encodeURIComponent(id)
        const data = await useApiMonOrga<RawAgence & { currentKey?: OrganizationKey | null }>(
            'PATCH',
            `agences/${encodedId}`,
            undefined,
            { changes: validated, base },
        )
        if (!data) return null
        return { agence: mapToAgence(data), currentKey: data.currentKey ?? null }
    }

    async function deleteAgence(id: string): Promise<boolean> {
        try {
            const encodedId = encodeURIComponent(id)
            await useApiMonOrga<unknown>('DELETE', `agences/${encodedId}`)
            return true
        } catch (error) {
            console.error(`Failed to delete agence with ID ${id}`, error)
            return false
        }
    }

    return {
        storeAgence,
        fetchAgenceById,
        updateAgence,
        patchAgence,
        mergeAgence,
        deleteAgence,
    }
}
